import type { Signal } from '@angular/core'
import { inject } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { tapResponse } from '@ngrx/operators'
import { signalStoreFeature, type, withMethods } from '@ngrx/signals'
import { Events, injectDispatch, withEventHandlers } from '@ngrx/signals/events'
import type { Observable } from 'rxjs'
import { exhaustMap, filter, groupBy, map, mergeMap, of, switchMap, take, takeUntil, takeWhile, tap } from 'rxjs'

import { isDefined } from '@app/core/is-defined'
import { isPaymentRequired } from '@app/core/is-payment-required'
import { nonCancelableRequest } from '@app/core/rxjs/non-cancelable-request'
import { CreditsStore } from '@app/modules/credits/store/credits.store'
import { conversationEvents } from '@app/modules/messaging/components/store/conversation.events'
import type { ConversationState } from '@app/modules/messaging/components/store/conversation.store'
import { CONVERSATION_STORE_CONFIG } from '@app/modules/messaging/components/store/conversation.store'
import { injectDefaultErrorHandler } from '@app/modules/messaging/components/store/default-error-handler'
import { sendingEvents } from '@app/modules/messaging/components/store/sending.events'
import type { Conversation } from '@app/modules/messaging/models/conversation.model'
import type { Message } from '@app/modules/messaging/models/message.model'
import { isFakeMessage } from '@app/modules/messaging/models/message.model'
import { ConversationUIService } from '@app/modules/messaging/services/conversation-ui.service'
import { ConversationService } from '@app/modules/messaging/services/conversation.service'
import { MessagingService } from '@app/modules/messaging/services/messaging.service'
import { CreditsNavigationService } from '@app/modules/navigation/services/credits-navigation.service'
import { PopoverService } from '@app/overlays/popover/popover.service'
import { MemberService } from '@app/services/member.service'

import { boostEvents } from './boost.events'
import { readReceiptsEvents } from '../../read-receipts/store/read-receipts.events'
import type { MessageBoostType } from '../models/message-boost-type'
import { FollowUpBoostService } from '../services/follow-up-boost.service'

export const boostFeature = () =>
  signalStoreFeature(
    { state: type<ConversationState>(), props: type<{ messages: Signal<Message[]> }>() },
    withMethods(
      (
        _store,
        conversationUIService = inject(ConversationUIService),
        creditsNavigationService = inject(CreditsNavigationService),
        dispatch = injectDispatch(boostEvents),
        source = inject(CONVERSATION_STORE_CONFIG).source,
        messagingService = inject(MessagingService),
      ) => ({
        attemptToBoostMessage: (message: Message, messageBoostType: MessageBoostType, conversation: Conversation) => {
          if (!messagingService.canAffordMessageBoost(conversation)) {
            const instance = conversationUIService.openBoostMessageInsufficientCreditsModal(conversation.profile)

            instance.onConfirm(() =>
              creditsNavigationService.visitCredits('sendBoostedMessage', {
                source,
                profile_id: conversation.profile.id,
              }),
            )
            return
          }
          dispatch.boost({ message, messageBoostType, profile_id: conversation.profile.id })
        },
      }),
    ),
    withMethods(store => {
      const messages$ = toObservable(store.messages)

      return {
        _getRealMessage: (fakeOrRealMessage: Message): Observable<Message> =>
          isFakeMessage(fakeOrRealMessage)
            ? messages$.pipe(
                map(messages => messages.find(message => message.fe_message_id === fakeOrRealMessage.fe_message_id)),
                takeWhile(isDefined),
                filter(message => !isFakeMessage(message)),
                take(1),
              )
            : of(fakeOrRealMessage),
      }
    }),
    withEventHandlers(
      (
        store,
        conversationService = inject(ConversationService),
        creditsNavigationService = inject(CreditsNavigationService),
        creditsStore = inject(CreditsStore),
        defaultErrorHandler = injectDefaultErrorHandler(),
        dispatch = injectDispatch(boostEvents),
        events = inject(Events),
        memberService = inject(MemberService),
        popoverService = inject(PopoverService),
        source = inject(CONVERSATION_STORE_CONFIG).source,
        conversationUIService = inject(ConversationUIService),
      ) => {
        const openMakeSureTheySeeItModal = (
          message: Message,
          messageBoostType: MessageBoostType,
          conversation: Conversation,
        ) => {
          const instance = conversationUIService.openMakeSureTheySeeItModal(conversation)
          instance.onConfirm(() => store.attemptToBoostMessage(message, messageBoostType, conversation))
        }

        return {
          boost$: events.on(boostEvents.boost).pipe(
            map(({ payload }) => payload),
            switchMap(payload =>
              store._getRealMessage(payload.message).pipe(map(message => ({ ...payload, message }))),
            ),
            groupBy(({ message }) => message.timestamp),
            mergeMap(group =>
              group.pipe(
                exhaustMap(({ message, messageBoostType, profile_id }) =>
                  conversationService.boostMessage(message.timestamp, messageBoostType, source).pipe(
                    tap(response => creditsStore.onCreditsSpent(response)),
                    nonCancelableRequest(),
                    tapResponse({
                      next: response => {
                        popoverService.show('nc-flash', 'Message Boosted')
                        dispatch.boostSuccess({
                          response,
                          message: store.messages().find(({ timestamp }) => message.timestamp === timestamp)!,
                          conversation: store.conversation()!,
                        })
                      },
                      error: error => dispatch.boostFailure({ error, profile_id }),
                      finalize: () => dispatch.boostEnd({ messageTimestamp: message.timestamp }),
                    }),
                    takeUntil(events.on(conversationEvents.select)),
                  ),
                ),
              ),
            ),
          ),

          onBoostFailure$: events.on(boostEvents.boostFailure).pipe(
            map(({ payload }) => payload),
            tap(({ error, profile_id }) => {
              if (isPaymentRequired(error)) {
                return creditsNavigationService.visitCredits('sendBoostedMessage', { source, profile_id })
              }

              defaultErrorHandler(error)
            }),
          ),

          openMakeSureTheySeeItModalOnUnlockReadReceiptsSuccess$: events
            .on(readReceiptsEvents.unlockReadReceiptsSuccess)
            .pipe(
              map(({ payload }) => payload),
              filter(({ message }) => MessagingService.isBoostable(message)),
              tap(({ message, conversation }) =>
                openMakeSureTheySeeItModal(message, 'post-read-receipts-modal', conversation),
              ),
            ),

          openMakeSureTheySeeItModalOnSendMessageSuccess$: events.on(sendingEvents.sendMessageSuccess).pipe(
            map(({ payload }) => payload),
            filter(() => !memberService.account.dependent),
            map(payload => ({
              ...payload,
              message: FollowUpBoostService.getMessageAllowedForFollowUp(payload.messages, store.messages()),
            })),
            tap(({ message, conversation }) => {
              if (message !== undefined) {
                openMakeSureTheySeeItModal(message, 'follow-up', conversation)
              }
            }),
          ),
        }
      },
    ),
  )
